'use client'
import React, { useState } from 'react'
import styled from 'styled-components'
import { useConfig } from '@/contexts/ConfigContext'
import { useLanguage } from '@/contexts/LanguageContext'

import ModuleContainer from './ModuleContainer'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import KeySelector from '@/components/CustomInput/KeySelector'
import { BUY_ITEMS } from '@/constants/buyItems'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import SellIcon from '@mui/icons-material/Sell'
import CloseIcon from '@mui/icons-material/Close'
import HistoryIcon from '@mui/icons-material/History'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { getItemIcon } from '@/constants/itemIcons'

const Custom_Controls = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    transition: opacity 0.2s ease;
    width: 100%;
`

const BindsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`

const BindCard = styled.div`
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 10px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    transition: all 0.2s;

    &:hover {
        background: rgba(255, 255, 255, 0.05);
        border-color: rgba(99, 102, 241, 0.3);
    }
`

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    width: 190px;
`

const BindNameInput = styled.input`
    background: transparent;
    border: none;
    border-bottom: 1px dashed rgba(255, 255, 255, 0.1);
    color: #fff;
    font-size: 0.75rem;
    font-weight: 500;
    padding: 8px 4px;
    font-family: var(--font-roboto);

    flex: 1;
    
    &:focus {
        outline: none;
        border-color: #6366f1;
    }

    &:disabled {
        border-bottom-color: transparent;
        cursor: default;
        opacity: 0.8;
    }
`

const CardBody = styled.div`
    display: flex;
    gap: 15px;
    align-items: flex-start;
    align-items: center;

`

const ItemsGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    flex: 1;
    margin-left: 10px;
    align-items: center;
`

const ItemBadge = styled.div`
    background: rgba(241, 165, 99, 0.1);
    border: 1px solid rgba(241, 172, 99, 0.2);
    color: #f6bfa5ff;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 0.7rem;
    display: flex;
    align-items: center;
    gap: 5px;

    svg {
        font-size: 14px;
        cursor: pointer;
        opacity: 0.6;
        &:hover { opacity: 1; }
    }
`

const ItemIcon = styled.img`
    width: 20px;
    height: 12px;
    object-fit: contain;
    filter: brightness(0) invert(1);
    opacity: 0.9;
`

const AddItemButton = styled.div`
    background: rgba(255, 255, 255, 0.05);
    border: 1px dashed rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.5);
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 0.7rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 5px;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: white;
        border-color: #6366f1;
    }
`

const DeleteBindButton = styled.div`
    color: rgba(239, 68, 68, 0.6);
    cursor: pointer;
    padding: 4px;
    transition: all 0.2s;
    &:hover { color: #ef4444; }
`

const ModalOverlay = styled.div`
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(5px);
`

const ModalContent = styled.div`
    background: #0d1117;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    width: 90%;
    max-width: 970px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 10px 40px rgba(0,0,0,0.5);

    
`

const ModalHeader = styled.div`
    padding: 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`

const ModalBody = styled.div`
    padding: 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    flex-direction: column;
`

const CategoryTitle = styled.div`
    font-size: 0.75rem;
    font-weight: 800;
    color: #6366f1;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 10px;
    width: 100%;
    flex-direction: column;
`

const SelectionGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 8px;
    width: 100%;
    flex-direction: column;
`

const SelectableItem = styled.div`
position: relative;
    background: ${({ $active }) => $active ? 'rgba(99, 102, 241, 0.2)' : 'rgba(255, 255, 255, 0.03)'};
    border: 1px solid ${({ $active }) => $active ? '#6366f1' : 'rgba(255, 255, 255, 0.05)'};
    padding: 10px;
    border-radius: 8px;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:hover {
        background: rgba(99,102,241, 0.15);
        border-color: #6366f1;
    }

    .price {
        font-size: 0.65rem;
        color: ${({ $active }) => $active ? '#a5a6f6' : 'rgba(255,255,255,0.3)'};
    }
`

const SelectableItemCheck = styled.div`
position: absolute;
right: 4px;
top: 4px;
`

const FooterActions = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 20px;
`

const AddMainButton = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(241, 146, 99, 0.1);
    color: #f19563ff;
    padding: 10px 20px;
    border-radius: 10px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid rgba(241, 146, 99, 0.2);
    transition: all 0.2s;

    &:hover {
        background: rgba(241, 146, 99, 0.2);
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(241, 135, 99, 0.2);
    }
`

const WizardProgress = styled.div`
    display: flex;
    gap: 8px;
    padding: 10px 20px;
    background: rgba(255, 255, 255, 0.02);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    width: 100%;
`;

const StepIndicator = styled.div`
    flex: 1;
    height: 4px;
    border-radius: 2px;
    background: ${({ $active, $completed }) => $active ? '#6366f1' : ($completed ? 'rgba(99, 102, 241, 0.4)' : 'rgba(255, 255, 255, 0.1)')};
    transition: all 0.3s ease;
`;

const ModalFooter = styled.div`
    padding: 15px 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
    display: flex;
    justify-content: space-between;
    background: rgba(0, 0, 0, 0.2);
    width: 100%;
    align-items: center;
`;

const WizardButton = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    background: ${({ $primary }) => $primary ? '#6366f1' : 'rgba(255, 255, 255, 0.05)'};
    color: ${({ $primary }) => $primary ? 'white' : 'rgba(255, 255, 255, 0.6)'};
    border: 1px solid ${({ $primary }) => $primary ? '#6366f1' : 'rgba(255, 255, 255, 0.1)'};

    &:hover {
        background: ${({ $primary }) => $primary ? '#4f46e5' : 'rgba(255, 255, 255, 0.1)'};
        color: white;
    }

    &.disabled {
        opacity: 0.3;
        pointer-events: none;
    }
`;



const WIZARD_STEPS = [
    { key: 'primary', label: 'WIZARD_STEP_PRIMARY', categories: ['rifles', 'smgs', 'heavy'] },
    { key: 'secondary', label: 'WIZARD_STEP_SECONDARY', categories: ['pistols'] },
    { key: 'grenades', label: 'WIZARD_STEP_GRENADES', categories: ['grenades'] },
    { key: 'gear', label: 'WIZARD_STEP_GEAR', categories: ['gear'] },
];

export default function BindsCustomModule({ id, initialExpanded }) {
    const { configData, updateConfig, activeModules, toggleModule, configIsLocked } = useConfig()
    const { t } = useLanguage()
    const [editingBindIndex, setEditingBindIndex] = useState(null)
    const [wizardStep, setWizardStep] = useState(0)

    const binds = configData.binds || {}
    const customBinds = binds.custom_binds || []
    const isActive = activeModules.includes('binds_custom')

    const updateCustomBinds = (newList) => {
        updateConfig('binds', 'custom_binds', newList)
    }

    const addNewBind = () => {
        const newBind = {
            id: Date.now(),
            name: '',
            key: '',
            items: []
        }
        updateCustomBinds([...customBinds, newBind])
    }

    const deleteBind = (index) => {
        const newList = [...customBinds]
        newList.splice(index, 1)
        updateCustomBinds(newList)
    }

    const updateBindField = (index, field, value) => {
        const newList = [...customBinds]
        newList[index] = { ...newList[index], [field]: value }
        updateCustomBinds(newList)
    }

    const toggleItemInBind = (index, itemId) => {
        const newList = [...customBinds]
        const bind = newList[index]
        const itemIndex = bind.items.indexOf(itemId)

        if (itemIndex > -1) {
            bind.items.splice(itemIndex, 1)
        } else {
            // For primary and secondary weapons, we might want to ensure only one is selected, 
            // but CS2 allows buying multiple. However, for the wizard UX, we usually want one.
            // But let's stay flexible.
            bind.items.push(itemId)
        }

        updateCustomBinds(newList)
    }

    const openWizard = (index) => {
        setEditingBindIndex(index);
        setWizardStep(0);
    }

    const nextStep = () => {
        if (wizardStep < WIZARD_STEPS.length - 1) {
            setWizardStep(wizardStep + 1);
        } else {
            setEditingBindIndex(null);
        }
    }

    const prevStep = () => {
        if (wizardStep > 0) {
            setWizardStep(wizardStep - 1);
        }
    }



    return (
        <ModuleContainer
            id={id}
            title={t('BINDS_CUSTOM_TITLE')}
            description={t('BINDS_CUSTOM_DESC')}
            icon={SellIcon}
            moduleKey="binds_custom"
            isActive={isActive}
            onToggle={() => toggleModule('binds_custom')}
            minHeight="200px"
            initialExpanded={initialExpanded}
        >
            <Custom_Controls isActive={isActive}>
                <BindsList>
                    {customBinds.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 0', opacity: 0.4 }}>
                            <ShoppingCartIcon style={{ fontSize: 40, marginBottom: 10 }} />
                            <p style={{ fontSize: '0.8rem' }}>{t('NO_CUSTOM_BINDS')}</p>
                        </div>
                    ) : (
                        customBinds.map((cb, idx) => (
                            <BindCard key={cb.id}>
                                <CardHeader>
                                    <BindNameInput
                                        value={cb.name}
                                        onChange={(e) => updateBindField(idx, 'name', e.target.value)}
                                        placeholder={t('BIND_NAME_PLACEHOLDER')}
                                        disabled={!isActive || configIsLocked}
                                    />
                                    {!configIsLocked && (
                                        <DeleteBindButton onClick={() => deleteBind(idx)}>
                                            <DeleteIcon style={{ fontSize: 18 }} />
                                        </DeleteBindButton>
                                    )}
                                </CardHeader>
                                <CardBody>
                                    <KeySelector
                                        value={cb.key}
                                        onChange={(v) => updateBindField(idx, 'key', v)}
                                        disabled={!isActive || configIsLocked}
                                        fieldPath={`binds.custom_binds.${idx}.key`}
                                        style={{ width: 100 }}
                                    />
                                    <ItemsGrid>
                                        {cb.items.map(itemId => {
                                            // Find item in constant
                                            let itemInfo = null;
                                            Object.values(BUY_ITEMS).some(cat => {
                                                const found = cat.find(i => i.id === itemId);
                                                if (found) { itemInfo = found; return true; }
                                                return false;
                                            });

                                            return (
                                                <ItemBadge key={itemId}>
                                                    {getItemIcon(itemId) && <ItemIcon src={getItemIcon(itemId)} alt="" />}
                                                    {itemInfo?.name || itemId.toUpperCase()}
                                                    {!configIsLocked && (
                                                        <CloseIcon onClick={() => toggleItemInBind(idx, itemId)} />
                                                    )}
                                                </ItemBadge>
                                            )
                                        })}
                                        {!configIsLocked && (
                                            <AddItemButton onClick={() => openWizard(idx)}>
                                                <AddIcon style={{ fontSize: 16 }} />
                                                {/* {t('SELECT_ITEMS')} */}
                                            </AddItemButton>
                                        )}
                                    </ItemsGrid>
                                </CardBody>
                            </BindCard>
                        ))
                    )}
                </BindsList>

                {!configIsLocked && (
                    <FooterActions>
                        <AddMainButton onClick={addNewBind} disabled={!isActive}>
                            <AddIcon />
                            {t('ADD_CUSTOM_BIND')}
                        </AddMainButton>
                    </FooterActions>
                )}
            </Custom_Controls>

            {
                editingBindIndex !== null && (
                    <ModalOverlay onClick={() => setEditingBindIndex(null)}>
                        <ModalContent onClick={e => e.stopPropagation()}>
                            <ModalHeader>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{ background: 'rgba(99, 102, 241, 0.1)', padding: 8, borderRadius: 8, display: 'flex' }}>
                                        <StorefrontIcon style={{ color: '#6366f1', fontSize: 20 }} />
                                    </div>
                                    <div style={{ width: '100%', gap: 20 }}>
                                        <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 800 }}>{t(WIZARD_STEPS[wizardStep].label)}</h3>
                                        <div style={{ fontSize: '0.65rem', opacity: 0.5, textTransform: 'uppercase', letterSpacing: 1, marginTop: 1 }}>
                                            Step {wizardStep + 1} of {WIZARD_STEPS.length}
                                        </div>
                                    </div>
                                </div>
                                <CloseIcon
                                    style={{ cursor: 'pointer', opacity: 0.4 }}
                                    onClick={() => setEditingBindIndex(null)}
                                />
                            </ModalHeader>

                            <WizardProgress>
                                {WIZARD_STEPS.map((_, i) => (
                                    <StepIndicator key={i} $active={i === wizardStep} $completed={i < wizardStep} />
                                ))}
                            </WizardProgress>

                            <ModalBody>
                                {WIZARD_STEPS[wizardStep].categories.map(cat => (
                                    <div key={cat} style={{ marginBottom: 20, flexDirection: 'column', width: '100%' }}>
                                        <CategoryTitle>{cat}</CategoryTitle>
                                        <SelectionGrid>
                                            {BUY_ITEMS[cat]?.map(item => {
                                                const isActiveItem = customBinds[editingBindIndex].items.includes(item.id);
                                                return (
                                                    <SelectableItem
                                                        key={item.id}
                                                        $active={isActiveItem}
                                                        onClick={() => toggleItemInBind(editingBindIndex, item.id)}
                                                    >
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: '100%' }}>
                                                            {getItemIcon(item.id) && (
                                                                <img
                                                                    src={getItemIcon(item.id)}
                                                                    alt=""
                                                                    style={{ width: '40px', height: '25px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
                                                                />
                                                            )}
                                                            <div style={{ textAlign: 'center', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
                                                                <div style={{ fontSize: '0.75rem', fontWeight: 700 }}>{item.name}</div>
                                                                <div style={{ fontSize: '0.65rem', opacity: 0.5 }}>${item.price}</div>
                                                            </div>
                                                        </div>
                                                        {isActiveItem && <SelectableItemCheck><CheckCircleIcon className="check-icon" /></SelectableItemCheck>}
                                                    </SelectableItem>
                                                )
                                            })}
                                        </SelectionGrid>
                                    </div>
                                ))}
                            </ModalBody>

                            <ModalFooter>
                                <WizardButton onClick={prevStep} className={wizardStep === 0 ? 'disabled' : ''}>
                                    <ArrowBackIcon style={{ fontSize: 16 }} />
                                    {t('WIZARD_BACK')}
                                </WizardButton>

                                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', marginRight: 15 }}>
                                        <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>
                                            {customBinds[editingBindIndex].items.length} {t('ITEMS_SELECTED')}
                                        </span>
                                    </div>

                                    {wizardStep < WIZARD_STEPS.length - 1 ? (
                                        <WizardButton $primary onClick={nextStep}>
                                            {t('WIZARD_NEXT')}
                                            <ArrowForwardIcon style={{ fontSize: 16 }} />
                                        </WizardButton>
                                    ) : (
                                        <WizardButton $primary onClick={() => setEditingBindIndex(null)}>
                                            {t('WIZARD_FINISH')}
                                            <CheckCircleIcon style={{ fontSize: 16 }} />
                                        </WizardButton>
                                    )}
                                </div>
                            </ModalFooter>
                        </ModalContent>
                    </ModalOverlay>
                )
            }
        </ModuleContainer >
    )
}
